import React, { Fragment, useEffect, useRef, useState } from 'react';
import cn from 'classnames';
import _ from 'lodash';

import { getShopProductUrl } from '../../helpers/helpers';
import { getAnswerableActions, getChoicesHint, getKeyAction, getLinkChoices, isTypingTarget } from '../../helpers/links_to_check_flow_helpers';
import { isMergeAnswer } from '../../helpers/links_to_check_likely_wrong_helpers';
import { getUrlDataLink } from '../../helpers/url_data_helpers';
import { useWindowKeydown } from '../../hooks/useWindowKeydown';
import { useLinksToCheckContext } from './LinksToCheckContext';
import AskForHelpSheet from './AskForHelpSheet';
import MergeSheet from './MergeSheet';
import SkipReasonSheet from './SkipReasonSheet';
import UndoLinkSheet from './UndoLinkSheet';

import './LinkCardActions.scss';

const PRESSED_MS = 130;

/** The card's answers, each with its key, and the sheets they open. */
const LinkCardActions = () => {
  const { link, pane, sheet, isKeysOpen, mergeBlockedReason, isUndoing, answer, notNow, openPane, openSheet } = useLinksToCheckContext();
  const [pressedShortcut, setPressedShortcut] = useState(null);
  const pressedTimerRef = useRef(null);

  // Actions
  const choices = getLinkChoices(link);
  const choicesHint = getChoicesHint(link);
  const pickProduct = link.pick || link.applied?.product;
  const openInNewTab = url => url && window.open(url, '_blank', 'noopener');
  const handlers = {
    link: () => answer({ kind: 'link', product: link.pick }),
    create: () => openPane('create'),
    search: () => openPane('search'),
    skip: action => (action.dismissReason ? answer({ kind: 'skip', reason: action.dismissReason }) : openSheet('skip')),
    help: () => openSheet('help'),
    open_page: () => openInNewTab(getUrlDataLink({ sampleUrl: link.url })),
    open_pick: () => openInNewTab(pickProduct && getShopProductUrl(pickProduct.id)),
    not_now: notNow,
    alert_right: () => answer({ kind: 'alert_right' }),
    alert_repoint: () => openPane('search'),
    undo_link: () => openSheet('undo_link'),
    undo_add: () => answer({ kind: 'undo_add' }),
    undo_skip: action => answer({ kind: 'undo_skip', reason: action.dismissReason }),
    merge: () => openSheet('merge'),
    keep_both: () => answer({ kind: 'keep_both' })
  };
  const getBlockedReason = action => (isMergeAnswer(link, { kind: action.key }) ? mergeBlockedReason : null);
  const actions = getAnswerableActions(link);
  const hasPrimary = actions[0]?.primary;

  // A clicked button gives focus back, so Enter or Space can't answer the next card by accident.
  const clickAction = (action, event) => {
    event.currentTarget.blur();
    handlers[action.key](action);
  };

  const pressAction = action => {
    clearTimeout(pressedTimerRef.current);
    setPressedShortcut(action.shortcut);
    pressedTimerRef.current = setTimeout(() => setPressedShortcut(null), PRESSED_MS);
    handlers[action.key](action);
  };

  useEffect(() => () => clearTimeout(pressedTimerRef.current), []);

  // Keyboard shortcuts, off while typing, while a pane or the keys overlay has the keyboard, and on a held
  // key's repeats, which would answer the next card unseen. A sheet keeps the answer keys to itself.
  useWindowKeydown(e => {
    if (pane || e.repeat || isKeysOpen || isUndoing || isTypingTarget(e.target) || e.metaKey || e.ctrlKey || e.altKey) return;
    const key = e.key.length === 1 ? e.key.toLowerCase() : e.key;
    const choice = !sheet && /^[1-9]$/.test(key) ? choices[Number(key) - 1] : null;
    const action = choice ? null : getKeyAction(link, key);
    if (sheet && !['open_page', 'open_pick'].includes(action?.key)) return;
    if (!choice && !handlers[action?.key]) return;
    e.preventDefault();
    if (choice) answer({ kind: 'link', product: choice });
    else if (getBlockedReason(action)) window.ALERT.info(getBlockedReason(action));
    else pressAction(action);
  });

  if (pane === 'create') return null;

  const renderButton = action => {
    const blockedReason = getBlockedReason(action);
    return (
      <Fragment key={action.key}>
        <button
          type='button'
          className={cn('ltc-btn', _.kebabCase(action.key), {
            primary: action.primary,
            ghost: action.key === 'not_now',
            pressed: pressedShortcut === action.shortcut
          })}
          disabled={isUndoing || Boolean(blockedReason)}
          onClick={event => clickAction(action, event)}
        >
          {action.display} <span className='ltc-key'>{action.shortcut}</span>
        </button>
        {blockedReason && <span className='actions-note'>{blockedReason}</span>}
      </Fragment>
    );
  };

  return (
    <footer className='links-to-check-card-actions' aria-busy={isUndoing}>
      {hasPrimary && renderButton(actions[0])}
      {choicesHint && (
        <span className='actions-hint'>
          {choicesHint.before}
          {choicesHint.keys.map(key => (
            <span key={key} className='ltc-key'>
              {key}
            </span>
          ))}
          {choicesHint.after}
        </span>
      )}
      {(hasPrimary ? actions.slice(1) : actions).map(renderButton)}
      {sheet === 'skip' && <SkipReasonSheet />}
      {sheet === 'help' && <AskForHelpSheet />}
      {sheet === 'undo_link' && <UndoLinkSheet />}
      {sheet === 'merge' && <MergeSheet />}
    </footer>
  );
};

export default LinkCardActions;
